import { choiceQuestionLabel, isChoiceElementType } from 'libs.consent.common';
// Re-exported so editor components keep importing choice helpers from here; the implementation lives
// in `libs.consent.common` because the MOM projector and the consent_bundler service need it too.
export { choiceQuestionLabel, isChoiceElementType };
/** What a TEXT_INPUT can collect, in the order the config form offers them. */
export const TEXT_INPUT_TYPE_OPTIONS = [
    { value: 'ec__text', label: 'Text' },
    { value: 'ec__numeric', label: 'Numeric' },
    { value: 'ec__email', label: 'Email' },
];
// TODO: translate descriptions
export const CONSENT_ELEMENT_TYPES = {
    SIGNATURE: {
        type: 'SIGNATURE',
        label: 'Signature',
        icon: 'pen',
        color: '#3B82F6',
        // 9pt Helvetica 2-line stamp (name+attestation / date+id). Wider than the
        // old 4-line drop because the first line now carries the role and action;
        // shorter because two lines need ~32pt plus padding, not four.
        defaultWidth: 0.46,
        defaultHeight: 0.05,
        description: 'Signature field for signing the document',
    },
    INITIALS: {
        type: 'INITIALS',
        label: 'Initials',
        icon: 'pen',
        color: '#8B5CF6',
        // Same 2-line stamp as signature; date+id is usually the widest initials line.
        defaultWidth: 0.45,
        defaultHeight: 0.05,
        description: 'Initials field',
    },
    DATE_INPUT: {
        type: 'DATE_INPUT',
        label: 'Date',
        icon: 'date',
        color: '#10B981',
        defaultWidth: 0.2,
        defaultHeight: 0.04,
        description: 'Date input field',
    },
    TEXT_INPUT: {
        type: 'TEXT_INPUT',
        label: 'Text',
        icon: 'text',
        color: '#F59E0B',
        // Same drop width as signature so the box spans the page like other
        // signer fields; height fits a wrapping 14px textarea (~two lines).
        defaultWidth: 0.46,
        defaultHeight: 0.05,
        description: 'Free text input field',
    },
    CHOICE: {
        type: 'CHOICE',
        label: 'Choice',
        icon: 'checkbox',
        color: '#EF4444',
        defaultWidth: 0.15,
        defaultHeight: 0.04,
        description: 'Multiple choice field',
    },
    SINGLE_SELECT: {
        type: 'SINGLE_SELECT',
        label: 'Single select',
        icon: 'radio',
        color: '#5F46CF',
        defaultWidth: 0.035,
        defaultHeight: 0.025,
        description: 'Single-choice (radio) question with independently-placed options',
    },
    MULTI_SELECT: {
        type: 'MULTI_SELECT',
        label: 'Checkbox',
        icon: 'checkbox',
        color: '#5F46CF',
        defaultWidth: 0.035,
        defaultHeight: 0.025,
        description: 'Multi-choice (checkbox) question with independently-placed options',
    },
};
export const ELEMENT_TYPE_LIST = Object.values(CONSENT_ELEMENT_TYPES);
const GENERIC_OVERLAY_RESIZE_MIN = { widthPct: 0.05, heightPct: 0.02 };
/**
 * Resize floor. New signature/initials drops cannot shrink below the 2-line 9pt stamp, a date
 * cannot shrink below a box that holds a spelled-out month, and a text overlay cannot shrink
 * below a 14px line — the live field and the archival stamp wrap inside the authored box
 * rather than clip or grow. Text width stays at the generic floor so the author can still narrow the box.
 */
export function overlayResizeMin(type) {
    if (type === 'SIGNATURE' || type === 'INITIALS' || type === 'DATE_INPUT') {
        const def = CONSENT_ELEMENT_TYPES[type];
        return { widthPct: def.defaultWidth, heightPct: def.defaultHeight };
    }
    if (type === 'TEXT_INPUT') {
        return {
            widthPct: GENERIC_OVERLAY_RESIZE_MIN.widthPct,
            heightPct: CONSENT_ELEMENT_TYPES.TEXT_INPUT.defaultHeight,
        };
    }
    return GENERIC_OVERLAY_RESIZE_MIN;
}
/**
 * Resize clamp. Boxes already smaller than the NFR min (published older templates)
 * keep their current size as the floor so a 1px nudge cannot snap them up to the
 * new drop size. Boxes at or above the min cannot shrink below it.
 */
export function clampOverlayResize(startPos, nextWidth, nextHeight, min) {
    const widthFloor = Math.min(startPos.widthPct, min.widthPct);
    const heightFloor = Math.min(startPos.heightPct, min.heightPct);
    return {
        widthPct: Math.max(widthFloor, Math.min(1 - startPos.xPct, nextWidth)),
        heightPct: Math.max(heightFloor, Math.min(1 - startPos.yPct, nextHeight)),
    };
}
/**
 * Element types shown in the editor toolbar, in Figma WIZ-3 order (nodes 463:14561 / 463:14714):
 * Signature, Initials, Checkbox (multi-select), Single select, Date, Text.
 *
 * This list is the authoring gate for what a signer can be asked to provide, so a type belongs
 * here only once the signing surface can actually satisfy it. Cortex's `completeSigner` rejects a
 * signer while any non-optional requested datum is unanswered, and the client offers no way to
 * remove one, so publishing an element the bundle cannot collect strands the document.
 */
export const TOOLBAR_ELEMENT_TYPES = [
    'SIGNATURE',
    'INITIALS',
    'MULTI_SELECT',
    'SINGLE_SELECT',
    'DATE_INPUT',
    'TEXT_INPUT',
];
/**
 * Locked Yes / No option specs for the single-select `yn_default` preset (SSP-4085).
 * Keys are stable integers; values are backend-only reference labels.
 */
export const YN_DEFAULT_OPTION_SPECS = [
    { key: 1, value: 'Y' },
    { key: 2, value: 'N' },
];
/** The optional "Not Applicable" option (key 3) that can be added to a `yn_default` single-select. */
export const YN_NA_OPTION_SPEC = { key: 3, value: 'N/A' };
/** Default box size (fraction of page) for a single placed option marker. */
export const CHOICE_OPTION_DEFAULT_SIZE = { widthPct: 0.035, heightPct: 0.025 };
/**
 * Initial {key, value} specs for a freshly-added choice element.
 * `yn_default` single-selects start with locked Yes/No; everything else starts with two blank options.
 */
export function initialChoiceOptionSpecs(type, preset) {
    if (type === 'SINGLE_SELECT' && preset === 'yn_default') {
        return YN_DEFAULT_OPTION_SPECS.map((spec) => ({ ...spec }));
    }
    return [
        { key: 1, value: '' },
        { key: 2, value: '' },
    ];
}
/**
 * Reassigns sequential keys (1, 2, 3…) to options in their current order. Used after adding/removing a
 * custom option so keys stay contiguous. Not used for the locked Yes/No preset, where keys are fixed.
 */
export function resequenceChoiceOptions(options) {
    return options.map((option, index) => ({ ...option, key: index + 1 }));
}
/**
 * Turns {key, value} specs into fully-positioned {@link ChoiceOption}s, staggered vertically from an
 * anchor point so each option starts visible on the page and can then be dragged independently.
 */
export function buildChoiceOptions(specs, page, anchor) {
    const { widthPct, heightPct } = CHOICE_OPTION_DEFAULT_SIZE;
    const gapPct = heightPct * 0.6;
    return specs.map((spec, index) => ({
        id: `opt-${Date.now()}-${spec.key}-${Math.random().toString(36).slice(2, 8)}`,
        key: spec.key,
        value: spec.value,
        page,
        placed: true,
        position: {
            xPct: Math.max(0, Math.min(1 - widthPct, anchor.xPct)),
            yPct: Math.max(0, Math.min(1 - heightPct, anchor.yPct + index * (heightPct + gapPct))),
            widthPct,
            heightPct,
        },
    }));
}
export const CONSENT_STORAGE_KEY = 'consent_documents_poc';
